import { fs } from '@/theme/tokens';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { APP_BASE, API_BASE } from '@/lib/api-base';
import { ApiError, errorMessage } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth';
import { FieldMapPanel } from '@/components/FieldMapPanel';

/**
 * Workspace onboarding wizard.
 *
 * Where the user comes from: they clicked "Payroll" in their uKnowva, the
 * plugin signed them in, and payroll found no workspace for their instance —
 * so `/api/auth/sso` registered an onboarding request and redirected here.
 *
 * There is no workspace to sign in to yet, so the page has no login. Every
 * call it makes is guarded by a per-request wizard secret the server keeps in
 * an HttpOnly cookie for the browser that started setup (or that an
 * administrator re-entered through uKnowva, or an operator link opened). The
 * request id in the URL is only a handle and opens nothing by itself. The
 * status endpoint still returns nothing sensitive — no database name, no
 * credentials, no employee data. Keep it that way.
 */

type StepStatus = 'PENDING' | 'RUNNING' | 'OK' | 'FAILED' | 'SKIPPED';

interface ProvisioningStep {
  key: string;
  label: string;
  status: StepStatus;
  detail: string | null;
}

interface DataSyncSource {
  title: string;
  status: 'PENDING' | 'RUNNING' | 'OK' | 'PARTIAL' | 'FAILED' | 'SKIPPED';
  rows: number;
  kind: string | null;
  import_status: string | null;
  imported: number;
  error: string | null;
  limited: number | null;
}

interface DataSyncSnapshot {
  status: string;
  summary: string | null;
  errors: number;
  warnings: number;
  sources: DataSyncSource[];
}

interface ProvisioningStatus {
  data_sync?: DataSyncSnapshot | null;
  request_id: string;
  reference: string;
  status: string;
  instance: string;
  steps: ProvisioningStep[];
  percent: number;
  next_action: string | null;
  blocked_reason: string | null;
  sso_key_available: boolean;
  /** Issued, but held back until the uKnowva API token has checked out. */
  sso_key_waiting_for_token?: boolean;
  created_at: string | null;
}

interface SsoKey {
  sso_shared_secret: string;
  instance: string;
  where: string;
  note: string;
}

/**
 * Request ids whose sign-in key this tab has already asked for.
 *
 * The endpoint hands the key over exactly once, so a second call gets 410 and
 * the key is lost to the user. React StrictMode mounts, unmounts and remounts
 * every component in development, which would burn that single read before the
 * page ever painted — and a `useRef` guard is recreated by that remount, so it
 * does not help. Module scope survives it.
 */
const KEY_FETCHED = new Set<string>();

/**
 * Operator setup links (`cli wizard-link`) carry a one-time secret in the URL
 * fragment. Posting it to `/claim` makes the server set this browser's wizard
 * cookie. Keyed by the secret at module scope for the same StrictMode reason
 * as KEY_FETCHED: the link works once, so it must be posted once.
 */
const CLAIMS = new Map<string, Promise<string | null>>();

function readClaimToken(): string | null {
  const m = /(?:^#|&)claim=([A-Za-z0-9_-]{20,128})/.exec(window.location.hash);
  return m?.[1] ?? null;
}

function claimOnce(requestId: string, token: string): Promise<string | null> {
  let p = CLAIMS.get(token);
  if (!p) {
    p = (async () => {
      try {
        const res = await fetch(`${API_BASE}/provisioning/${requestId}/claim`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }) });
        if (res.ok) return null;
        const body = (await res.json().catch(() => ({}))) as { detail?: string };
        return body.detail || `HTTP ${res.status}`;
      } catch (err) {
        return (err as Error).message;
      }
    })();
    CLAIMS.set(token, p);
  }
  return p;
}

const POLL_MS = 2000;

/** Statuses where nothing more will change, so polling should stop. */
const TERMINAL = new Set(['COMPLETED', 'FAILED', 'REJECTED']);

const KIND_LABEL: Record<string, string> = {
  department: 'Departments',
  designation: 'Designations',
  shift: 'Shifts',
  leave_type: 'Leave types',
  location: 'Locations',
  grade: 'Grades' };

function sourceStatus(s: DataSyncSource): StepStatus {
  if (s.status === 'PARTIAL') return 'FAILED';
  return s.status as StepStatus;
}

/**
 * Every master fetched during setup, as it happens. A master that fails is
 * shown with its reason and setup carries on with the rest — the dashboard's
 * "Sync uKnowva data" window can fetch it again once fixed.
 */
function DataSyncPanel({ sync }: { sync: DataSyncSnapshot }) {
  if (!sync.sources.length) return null;
  const done = sync.sources.filter((s) => !['PENDING', 'RUNNING'].includes(s.status)).length;
  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
      <Typography variant="subtitle2">Master data from uKnowva</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {done} of {sync.sources.length} fetched
        {sync.errors ? ` · ${sync.errors} problem(s). Setup continues with what arrived` : ''}
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {sync.sources.map((s, i) => (
          <Box component="li" key={`${s.title}-${i}`}
            sx={{ display: 'flex', gap: 1.25, py: 0.5, alignItems: 'flex-start' }}>
            <StepIcon status={sourceStatus(s)} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">
                {s.title}
                <Typography component="span" variant="caption" color="text.secondary">
                  {': '}{s.rows.toLocaleString()} row(s)
                  {s.import_status === 'IMPORTED' && s.kind
                    ? ` · ${s.imported} imported as ${KIND_LABEL[s.kind] ?? s.kind}` : ''}
                  {s.import_status === 'NEEDS_MAPPING'
                    ? ' · stored; map its columns on the dashboard to import it' : ''}
                  {s.status === 'PARTIAL' ? ' · only partly fetched' : ''}
                  {s.limited
                    ? ` · first ${s.limited.toLocaleString()} rows (setup limit); a full sync from the dashboard brings the rest`
                    : ''}
                </Typography>
              </Typography>
              {s.error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', wordBreak: 'break-word' }}>
                  {s.error}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function StepIcon({ status }: { status: StepStatus }) {
  const sx = { fontSize: fs.h2, mt: '2px' };
  switch (status) {
    case 'OK':
      return <CheckCircleIcon color="success" sx={sx} />;
    case 'FAILED':
      return <ErrorOutlineIcon color="error" sx={sx} />;
    case 'SKIPPED':
      return <RemoveCircleOutlineIcon color="disabled" sx={sx} />;
    case 'RUNNING':
      return <CircularProgress size={18} sx={{ mt: '3px' }} />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" sx={sx} />;
  }
}

export function ProvisioningPage() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  // `/api/auth/sso` sends `?continue=1` when it cannot tell whether this
  // browser started setup (the wizard cookie never reaches it). Ask the
  // server which setup this browser holds before showing anything.
  if (!requestId && searchParams.get('continue')) {
    return <ContinueSetup fallbackError={searchParams.get('sso_error')} />;
  }
  return <Wizard requestId={requestId} />;
}

/**
 * Opens the newest setup this browser holds, or falls through to the login
 * page with the SSO message it would have shown anyway.
 */
function ContinueSetup({ fallbackError }: { fallbackError: string | null }) {
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let target: string | null = null;
      try {
        const res = await fetch(`${API_BASE}/provisioning/mine`);
        if (res.ok) {
          const data = (await res.json()) as { requests: { request_id: string }[] };
          target = data.requests[0]?.request_id ?? null;
        }
      } catch {
        // Nothing to resume from here; the login page explains.
      }
      if (cancelled) return;
      navigate(
        target
          ? `${APP_BASE}/provisioning?request=${encodeURIComponent(target)}`
          : `${APP_BASE}/login${fallbackError ? `?sso_error=${encodeURIComponent(fallbackError)}` : ''}`,
        { replace: true },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, fallbackError]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

function Wizard({ requestId }: { requestId: string | null }) {
  const navigate = useNavigate();

  const [status, setStatus] = useState<ProvisioningStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  // An operator link's secret, read once before anything strips the fragment.
  const [claimToken] = useState(readClaimToken);
  const [claimed, setClaimed] = useState(!claimToken);
  // The server refused this browser (no wizard secret, or unknown request):
  // polling again cannot change that, so the page stops and says why.
  const [refused, setRefused] = useState(false);
  const [ssoKey, setSsoKey] = useState<SsoKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    hrms_api_token: '',
    entity_name: '',
    country_code: 'IN',
    currency_code: 'INR' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  // Bumped by a successful resume. Polling stops once a run is FAILED, so a
  // retry has to start it again or the checklist freezes on "Retrying…".
  const [pollEpoch, setPollEpoch] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Redeem an operator setup link before the first poll, and take its secret
  // out of the address bar straight away: it is single-use and must not stay
  // in history.
  useEffect(() => {
    if (!claimToken || !requestId) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    let cancelled = false;
    claimOnce(requestId, claimToken).then((problem) => {
      if (cancelled) return;
      if (problem) {
        setError(problem);
        setRefused(true);
      }
      setClaimed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [claimToken, requestId]);

  useEffect(() => {
    if (!requestId) {
      setError('No onboarding request was provided in the link.');
      return;
    }
    if (!claimed || refused) return;

    let cancelled = false;

    // setTimeout chained after each response rather than setInterval: a slow
    // or hung request must not stack up a queue of overlapping polls.
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/provisioning/${requestId}/status`);
        if (res.status === 403 || res.status === 404) {
          const body = await res.json().catch(() => null);
          if (cancelled) return;
          setError(errorMessage(new ApiError(res.status, body)));
          setRefused(true);
          return;
        }
        if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
        const data = (await res.json()) as ProvisioningStatus;
        if (cancelled) return;
        setStatus(data);
        setError(null);
        if (!TERMINAL.has(data.status)) {
          timer.current = setTimeout(poll, POLL_MS);
        }
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err));
        // Keep polling through a transient network blip — the setup is still
        // running on the server whether or not this tab can reach it.
        timer.current = setTimeout(poll, POLL_MS * 3);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [requestId, pollEpoch, claimed, refused]);

  // Collect the one-time sign-in key the moment setup has issued one. Done
  // here rather than behind a button because the key is the thing the user
  // came for: if the tab is closed before they press anything, re-issuing it
  // costs an operator a CLI round-trip.
  useEffect(() => {
    if (!requestId || !status?.sso_key_available) return;
    if (KEY_FETCHED.has(requestId)) return;
    KEY_FETCHED.add(requestId);

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/provisioning/${requestId}/sso-key`);
        if (!res.ok) return;                    // 410 = already shown; not an error here
        const data = (await res.json()) as SsoKey;
        if (!cancelled) setSsoKey(data);
      } catch {
        // Leave the key uncollected rather than showing a half-state; the
        // operator can re-issue one. Nothing here is worth an error banner.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestId, status?.sso_key_available]);

  // Seed the entity name from the instance host once, as a starting point the
  // user can overwrite — "acme" from acme.uknowva.com beats an empty box.
  useEffect(() => {
    if (!status?.instance) return;
    setForm((f) => {
      if (f.entity_name) return f;
      const guess = status.instance.split('.')[0] ?? '';
      return { ...f, entity_name: guess.charAt(0).toUpperCase() + guess.slice(1) };
    });
  }, [status?.instance]);

  const copyKey = async () => {
    if (!ssoKey) return;
    try {
      await navigator.clipboard.writeText(ssoKey.sso_shared_secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const resume = async () => {
    if (!requestId) return;
    setResuming(true);
    try {
      const res = await fetch(`${API_BASE}/provisioning/${requestId}/resume`, {
        method: 'POST' });
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      // Restart the poll loop: it polls immediately, then keeps going until
      // the resumed run settles.
      setPollEpoch((n) => n + 1);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setResuming(false);
    }
  };

  const submitConnect = async () => {
    if (!requestId) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await fetch(`${API_BASE}/provisioning/${requestId}/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form) });
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      // The poll picks the resumed pipeline up on its next tick.
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const awaitingApproval = status?.status === 'PENDING_APPROVAL';
  const failed = status?.status === 'FAILED';
  const rejected = status?.status === 'REJECTED';
  const done = status?.status === 'COMPLETED';
  const needsConnect = status?.next_action === 'CONNECT_HRMS';
  const needsConfig = status?.next_action === 'NEEDS_CONFIG';
  const needsFieldMap = status?.next_action === 'MAP_FIELDS';
  const stoppedAt = status?.steps.find((s) => s.status === 'FAILED')?.label;
  // Steps that failed without stopping setup (the HRMS imports).
  const unfinished = done ? (status?.steps.filter((s) => s.status === 'FAILED') ?? []) : [];
  // Set only when the user arrived here from a signed-in SSO callback, i.e.
  // their workspace already exists and only the import is unfinished. Parking
  // an administrator on this page with no way forward would lock them out of
  // a workspace that works.
  const signedIn = Boolean(useAuthStore((s) => s.token));

  return (
    <Box
      sx={{
        // This page owns its scrolling. The theme pins html/body/#root to the
        // viewport with `overflow: hidden` because the AppShell scrolls its
        // own panes — correct inside the app, but it means a standalone page
        // like this one cannot scroll at all: the checklist plus the
        // field-mapping form is roughly 2,700px and everything below the fold
        // was simply unreachable. A fixed-height box that scrolls internally
        // restores it without touching the shell's rule.
        height: '100vh',
        overflowY: 'auto',
        display: 'flex',
        // Top-aligned, not centred: a centred flex child that overflows its
        // container cannot be scrolled back up to.
        alignItems: 'flex-start',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: 2 }}
    >
      <Paper sx={{ maxWidth: 860, width: '100%', p: { xs: 2, sm: 4 }, my: 2 }} elevation={2}>
        <Typography variant="h5" gutterBottom>
          Setting up your payroll workspace
        </Typography>

        {status && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {status.instance} &nbsp;·&nbsp; reference {status.reference}
          </Typography>
        )}

        {signedIn && !done && (
          <Button
            size="small"
            onClick={() => navigate(`${APP_BASE}/`, { replace: true })}
            sx={{ mb: 1 }}
          >
            Continue to your workspace
          </Button>
        )}

        {error && (!status || refused) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {error && status && !refused && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Lost contact with the server. Still retrying. Setup continues in the
            background. ({error})
          </Alert>
        )}

        {!status && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {status && (
          <>
            {awaitingApproval && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Your workspace request has been sent to the payroll team. Nothing
                is created until someone approves it. You can close this page and
                come back to this link later in this browser.
              </Alert>
            )}
            {rejected && (
              <Alert severity="error" sx={{ mt: 2 }}>
                This request was not approved. Please contact your payroll team
                and quote reference {status.reference}.
              </Alert>
            )}
            {failed && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={resume} disabled={resuming}>
                    {resuming ? 'Retrying…' : 'Try again'}
                  </Button>
                }
              >
                Setup stopped before it finished
                {stoppedAt ? <> at <strong>{stoppedAt}</strong></> : null}. If your
                payroll team has addressed the cause, retry from where it stopped.
                Completed steps are not repeated. Otherwise quote reference{' '}
                {status.reference}.
              </Alert>
            )}
            {done && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Your workspace is ready. Go back to uKnowva and click Payroll
                again to sign in.
              </Alert>
            )}
            {unfinished.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Some data did not come across:{' '}
                <strong>{unfinished.map((s) => s.label).join(', ')}</strong>. Setup
                carried on without it, and your payroll team can import it again.
                Quote reference {status.reference}.
              </Alert>
            )}

            {needsFieldMap && requestId && (
              <FieldMapPanel
                requestId={requestId}
                onSaved={() => {
                  // Resumed server-side by the POST; pull the new state now so
                  // the checklist moves without waiting for the next tick.
                  void (async () => {
                    const s = await fetch(
                      `${API_BASE}/provisioning/${requestId}/status`,
                    );
                    if (s.ok) setStatus((await s.json()) as ProvisioningStatus);
                  })();
                }}
              />
            )}

            {needsConfig && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Setup is paused. It needs a setting only your payroll team can
                store. Nothing is lost: it continues from here once that is done.
                {status.blocked_reason && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    {status.blocked_reason}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  Quote reference {status.reference}.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={resume}
                  disabled={resuming}
                  sx={{ mt: 1.5 }}
                >
                  {resuming ? 'Resuming…' : 'Resume setup'}
                </Button>
              </Alert>
            )}

            {ssoKey && (
              <Paper
                variant="outlined"
                sx={{ mt: 2, p: 2, borderColor: 'warning.main', bgcolor: 'warning.50' }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Your workspace sign-in key: shown once
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Copy this now and paste it into {ssoKey.where}. Until you do,
                  signing in from uKnowva will be rejected.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    value={ssoKey.sso_shared_secret}
                    size="small"
                    fullWidth
                    slotProps={{ input: { readOnly: true, sx: {} } }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={copyKey}
                    sx={{ flexShrink: 0 }}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </Stack>
              </Paper>
            )}

            {status.sso_key_waiting_for_token && !ssoKey && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Your workspace sign-in key is ready. It is shown here once your
                uKnowva API token has been connected and checked, which proves
                this setup belongs to {status.instance}.
              </Alert>
            )}

            {needsConnect && (
              <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Connect your uKnowva
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Setup needs two things it cannot work out on its own: an API
                  token from your uKnowva, and the legal entity your employees
                  will belong to. Nothing is imported until both are supplied.
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="uKnowva API token"
                    value={form.hrms_api_token}
                    onChange={(e) => setForm({ ...form, hrms_api_token: e.target.value })}
                    size="small"
                    fullWidth
                    type="password"
                    helperText="uKnowva → Admin → Manage API Tokens → Payroll APIs"
                  />
                  <TextField
                    label="Legal entity name"
                    value={form.entity_name}
                    onChange={(e) => setForm({ ...form, entity_name: e.target.value })}
                    size="small"
                    fullWidth
                    helperText="The company that employs these people and files their returns"
                  />
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Country"
                      value={form.country_code}
                      onChange={(e) =>
                        setForm({ ...form, country_code: e.target.value.toUpperCase() })
                      }
                      size="small"
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Currency"
                      value={form.currency_code}
                      onChange={(e) =>
                        setForm({ ...form, currency_code: e.target.value.toUpperCase() })
                      }
                      size="small"
                      sx={{ width: 120 }}
                    />
                  </Stack>
                  {formError && <Alert severity="error">{formError}</Alert>}
                  <Box>
                    <Button
                      variant="contained"
                      onClick={submitConnect}
                      disabled={
                        submitting ||
                        !form.hrms_api_token.trim() ||
                        !form.entity_name.trim()
                      }
                    >
                      {submitting ? 'Connecting…' : 'Connect and import'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    The entity is created with your name, country and currency.
                    PF, ESIC and PT registration numbers cannot be guessed. An
                    administrator adds those before payroll can be run.
                  </Typography>
                </Stack>
              </Paper>
            )}

            <Box sx={{ mt: 3, mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={status.percent}
                color={failed || rejected ? 'error' : 'primary'}
              />
              <Typography variant="caption" color="text.secondary">
                {status.percent}% complete
              </Typography>
            </Box>

            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 2 }}>
              {status.steps.map((step) => (
                <Box
                  component="li"
                  key={step.key}
                  sx={{ display: 'flex', gap: 1.5, py: 0.75, alignItems: 'flex-start' }}
                >
                  <StepIcon status={step.status} />
                  <Box>
                    <Typography
                      variant="body2"
                      color={step.status === 'PENDING' ? 'text.disabled' : 'text.primary'}
                    >
                      {step.label}
                    </Typography>
                    {step.detail && (
                      <Typography variant="caption" color="text.secondary">
                        {step.detail}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>

            {status.data_sync && <DataSyncPanel sync={status.data_sync} />}

            {done && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
                Your workspace has your people and org structure. Before payroll
                can be run, an administrator still needs to set up pay items,
                salary structures, pay groups and the pay calendar.
              </Typography>
            )}
          </>
        )}

        <Box sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`${APP_BASE}/login`, { replace: true })}
          >
            Go to login
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
